import { AuthFooterLink, AuthMessage, Button } from '@protoss/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { withDevWorkspaceParam } from '../../lib/url';
import './auth-pages.css';

/**
 * Email Action Page  (/auth/action)
 *
 * Handles Firebase out-of-band (OOB) email action links - the URLs embedded
 * in Firebase-sent emails (email verification and password reset).
 *
 * Expected query parameters (set by Firebase):
 *   mode        - action type: "verifyEmail" | "resetPassword" | "recoverEmail"
 *   oobCode     - the one-time action code
 *   continueUrl - optional continue URL (passed through from actionCodeSettings)
 *   apiKey      - Firebase project API key (not used directly)
 *
 * For resetPassword the user is redirected to continueUrl (invites:
 * /auth/set-password?inviteId=..., plain resets: /reset-password) with the
 * oobCode appended, or to the same-origin /reset-password when there is none.
 *
 * This route is intentionally unguarded - it must work for both
 * authenticated and unauthenticated visitors.
 */

type PageState = 'loading' | 'success' | 'error' | 'unsupported';

function mapVerifyError(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);
  if (msg.includes('expired-action-code') || msg.includes('invalid-action-code')) {
    return 'This verification link is invalid or has expired. Please request a new one.';
  }
  if (msg.includes('user-disabled')) {
    return 'This account has been disabled. Please contact support.';
  }
  return 'Something went wrong while verifying your email. Please try again.';
}

export function EmailActionPage() {
  const { verifyEmail, isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  const inviteIdFromContinueUrl = React.useMemo(() => {
    if (!continueUrl) return null;
    try {
      const parsed = new URL(continueUrl);
      return parsed.searchParams.get('inviteId');
    } catch {
      return null;
    }
  }, [continueUrl]);

  const [pageState, setPageState] = React.useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // Handle mode-based Firebase action links (verifyEmail, resetPassword).
  // Runs once on mount - the oobCode is a one-time token that must not be
  // applied more than once.
  //
  // Firebase sends email links directly to this page (with mode, oobCode, apiKey, etc.
  // as query params) when the Firebase Console → Authentication → Email Templates →
  // "Customize action URL" is set to https://<your-domain>/auth/action.
  // Without that Console setting, Firebase's hosted page intercepts the link first and
  // redirects here as continueUrl without any action params - the second effect below
  // handles that legacy/fallback case.
  React.useEffect(() => {
    if (mode === 'resetPassword') {
      // If continueUrl is present (invite flow), it already contains the workspace-specific
      // origin and /auth/set-password?inviteId=... - redirect there and append oobCode so the
      // user lands on the correct subdomain (e.g. rapidcloud-uat.cosow.ai, not cosow-uat.cosow.ai).
      // Plain resets from /forgot-password carry continueUrl = <origin>/reset-password.
      // With no continueUrl (e.g. a reset sent from the Firebase Console), fall back
      // to the same-origin /reset-password page. /auth/set-password is invite-only.
      if (continueUrl) {
        try {
          const target = new URL(continueUrl);
          if (oobCode) target.searchParams.set('oobCode', oobCode);
          window.location.replace(target.toString());
          return;
        } catch {
          // continueUrl was malformed - fall through to relative redirect below
        }
      }
      const params = new URLSearchParams();
      if (oobCode) params.set('oobCode', oobCode);
      window.location.replace(withDevWorkspaceParam(`/reset-password?${params.toString()}`));
      return;
    }

    if (mode !== 'verifyEmail') {
      if (mode !== null) {
        // Mode is present but not a supported action type (e.g. recoverEmail).
        // Surface the unsupported state immediately rather than leaving the page
        // stuck on the loading state indefinitely.
        setPageState('unsupported');
      }
      // When mode is absent, the second effect handles the Firebase hosted-page
      // redirect fallback once auth has finished loading.
      return;
    }

    if (!oobCode) {
      setErrorMessage('The verification link is missing required information.');
      setPageState('error');
      return;
    }

    let cancelled = false;

    const apply = async () => {
      try {
        await verifyEmail(oobCode);
        if (!cancelled) setPageState('success');
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(mapVerifyError(error));
          setPageState('error');
        }
      }
    };

    void apply();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteIdFromContinueUrl, mode, oobCode]);

  // Handle the case where Firebase processed the verification on its hosted
  // page and redirected here as the continueUrl - without preserving the
  // mode / oobCode params. In that case mode is absent but Firebase has
  // already marked the email as verified. We wait for auth to finish
  // initialising before reading the verified state.
  React.useEffect(() => {
    if (mode) return; // already handled by the effect above
    if (authLoading) return;
    setPageState(user?.emailVerified ? 'success' : 'unsupported');
  }, [authLoading, mode, user?.emailVerified]);

  if (pageState === 'loading') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader
            title="Verifying your email…"
            subtitle="Please wait while we verify your email address."
          />
        </AuthCard>
      </AuthLayout>
    );
  }

  if (pageState === 'success') {
    // If auth state is still resolving, wait before deciding where to send the user.
    const continueHref = !authLoading && isAuthenticated ? '/' : '/signin';
    const continueLabel = !authLoading && isAuthenticated ? 'Continue' : 'Go to sign in';

    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader
            title="Email verified successfully"
            subtitle="You can now continue to the application."
          />
          <div className="auth-form-container">
            <AuthMessage variant="success">
              Your email has been verified and your account is ready.
            </AuthMessage>
            <Button
              variant="primary"
              onClick={() => {
                window.location.replace(continueHref);
              }}
            >
              {continueLabel}
            </Button>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (pageState === 'unsupported') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Unknown action" subtitle="This link is not recognised." />
          <div className="auth-footer-text">
            <AuthFooterLink href="/signin">Back to sign in</AuthFooterLink>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // error state
  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader title="Verification link invalid or expired" />
        <div className="auth-form-container">
          <AuthMessage variant="error">
            {errorMessage ?? 'This verification link is invalid or has expired.'}
          </AuthMessage>
          <AuthFooterLink href="/verify-email-sent">Resend verification email</AuthFooterLink>
        </div>
        <div className="auth-footer-text">
          <AuthFooterLink href="/signin">Back to sign in</AuthFooterLink>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
